import filenamify from 'filenamify';
import slugify from 'slugify';

/**
 * Sanitizes a filename by removing accents and ensuring it's safe for file systems
 * @param filename - The original filename to sanitize
 * @returns The sanitized filename
 */
export const sanitizeFilename = (filename: string): string => {
  if (!filename || typeof filename !== 'string') {
    return '';
  }
  // Normalize to NFD (decomposed), then remove combining diacritical marks
  const withoutAccents = filename.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '');
  // Then use filenamify to ensure it's safe for file systems
  return filenamify(withoutAccents, { replacement: '' });
};

/**
 * Sanitizes a document name specifically for document uploads
 * Converts to lowercase after sanitization as per existing code patterns
 * @param documentName - The original document name to sanitize
 * @returns The sanitized document name in lowercase
 */
export const sanitizeDocumentName = (documentName: string): string => {
  if (!documentName || typeof documentName !== 'string') {
    return '';
  }

  return sanitizeFilename(documentName).toLowerCase();
};

  // Normalize to a safe ASCII-ish filename to keep client + server signing logic aligned.
export const asciiNormalizeFilename = (originalName: string): string => {
  if (!originalName) return 'file';
  // Hard limit to mitigate pathological extremely long inputs that could amplify any regex cost.
  const MAX_INPUT_CHARS = 4096; // defensive cap
  if (originalName.length > MAX_INPUT_CHARS) {
    originalName = originalName.slice(0, MAX_INPUT_CHARS);
  }
  // If the filename is already ASCII-only, contains only safe characters, and no leading path parts, leave it unchanged.
  // (Ensures we don't unnecessarily modify simple safe names.)
  const safePattern = /^[A-Za-z0-9._-]+(\.[A-Za-z0-9]{1,10})?$/;
  const hasPathSep = /[\\/]/.test(originalName);
  // Allow spaces between safe tokens as an additional "safe" pattern (no transformation if only spaces separate tokens)
  const safeWithSpacesPattern = /^[A-Za-z0-9._-]+(?: [A-Za-z0-9._-]+)*(\.[A-Za-z0-9]{1,10})?$/;
  // Extended safe: allow commas, parentheses, and multiple hyphen runs; we only transform if other disallowed chars appear.
  const extendedSafePattern = /^[A-Za-z0-9._(),-]+(\.[A-Za-z0-9]{1,10})?$/;
  // Extended with spaces: combine punctuation (comma, parens, hyphens) and space-separated groups
  const extendedWithSpacesPattern = /^[A-Za-z0-9._(),-]+(?: [A-Za-z0-9._(),-]+)*(\.[A-Za-z0-9]{1,10})?$/;
  if (!hasPathSep) {
    // Quick diacritic check: compare to NFD-stripped version
    const stripped = originalName.normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '');
    if (
      stripped === originalName && (
        safePattern.test(originalName) ||
        safeWithSpacesPattern.test(originalName) ||
        extendedSafePattern.test(originalName) ||
        extendedWithSpacesPattern.test(originalName)
      )
    ) {
      return originalName; // Already safe; preserve original casing & punctuation
    }
  }
  const lastDot = originalName.lastIndexOf('.');
  let base = lastDot > 0 ? originalName.slice(0, lastDot) : originalName;
  let ext = lastDot > 0 ? originalName.slice(lastDot).toLowerCase() : '';
  if (base.startsWith('.') && !ext) base = base.slice(1);
  // Use slugify for robust folding; preserve underscores & dots; remove other symbols
  // slugify internally lowercases by default; set lower true, strict true to drop invalid chars
  let slug = slugify(base, { lower: true, strict: true, locale: 'en' });
  // Custom folds not handled by slugify (ß -> ss)
  slug = slug.replaceAll('ß', 'ss');
  // Collapse repeated separators
  // Collapse repeated separators (simple linear regex; character class only -> no catastrophic backtracking)
  slug = slug.replaceAll(/[-_]{2,}/g, '-');
  // Trim leading/trailing hyphens WITHOUT alternation to avoid any theoretical backtracking concerns.
  // (These loops are O(n) and safer than a more complex anchored alternation regex.)
  while (slug.startsWith('-')) slug = slug.slice(1);
  while (slug.endsWith('-')) slug = slug.slice(0, slug.length - 1);
  if (!slug) slug = 'file';
  const reserved = new Set(['con','prn','aux','nul','com1','com2','com3','com4','com5','com6','com7','com8','com9','lpt1','lpt2','lpt3','lpt4','lpt5','lpt6','lpt7','lpt8','lpt9']);
  if (reserved.has(slug)) slug = slug + '-file';
  // Hash for uniqueness
  let h = 0x811c9dc5; // FNV-1a
  for (let i = 0; i < originalName.length; ) {
    const cp = originalName.codePointAt(i) as number; // codePointAt returns number | undefined; originalName length check ensures defined
    h ^= cp;
    h = (h * 0x01000193) >>> 0; // FNV-1a prime
    i += cp > 0xffff ? 2 : 1; // advance by code point length (handle surrogate pairs)
  }
  const hash = h.toString(16).slice(0,6);
  const MAX_BASE = 80;
  if (slug.length > MAX_BASE) slug = slug.slice(0, MAX_BASE);
  slug = `${slug}-${hash}`;
  if (ext && !/^\.[A-Za-z0-9]{1,10}$/.test(ext)) ext = '';
  const finalName = slug + ext;
  return finalName.length > 200 ? finalName.slice(0,180) + '-' + hash + (ext || '') : finalName;
};